// Documentation. Chaque article décrit du comportement réellement implémenté
// dans backend/app : rien n'est documenté « par anticipation ».

export const DOC_TABS = [
  { label: "Vue d'ensemble", tab: "overview" },
  { label: "API REST", tab: "api" },
  { label: "Sécurité", tab: "security" },
  { label: "Nouveautés", to: "/nouveautes" },
];

export const API_BASE = "https://api.performcustom.com";

export const DOCS = [
  {
    slug: "", tab: "overview", group: "Commencer", nav: "Vue d'ensemble",
    breadcrumb: "Commencer", title: "Vue d'ensemble", subtitle: "Construire avec Perform.",
    sections: [
      { id: "intro", blocks: [
        { t: "p", v: "Perform est une plateforme de coaching esport : un coach accompagne des joueurs sur cinq jeux compétitifs. Cette documentation décrit la plateforme, son API HTTP et son modèle de sécurité." },
        { t: "p", v: "Tout ce qui est décrit ici correspond au comportement réel du service en production. Les fonctionnalités encore en chantier sont explicitement signalées comme telles." },
      ]},
      { id: "parties", heading: "Les trois surfaces", blocks: [
        { t: "p", v: "La plateforme s'utilise par trois entrées distinctes :" },
        { t: "ul", v: [
          "**L'application web** — l'espace joueur et l'espace coach, sur performcustom.com.",
          "**L'API HTTP** — pour lire et écrire vos données depuis vos propres outils. Voir la section API REST.",
          "**Le Trust Center** — ce que nous stockons, comment, et pendant combien de temps.",
        ]},
      ]},
      { id: "etat", heading: "État actuel", blocks: [
        { t: "note", kind: "info", v: "L'API publique couvre aujourd'hui l'authentification et l'état du service. Les points d'entrée pour les séances et les paiements arrivent ensuite. Cette page est mise à jour à chaque livraison." },
      ]},
      { id: "suite", heading: "Par où commencer", blocks: [
        { t: "p", v: "Si vous découvrez la plateforme, suivez le démarrage rapide : il crée un compte et ouvre une session en deux appels. Si vous cherchez un point d'entrée précis, allez directement à la référence des endpoints." },
      ]},
    ],
  },
  {
    slug: "demarrage", tab: "overview", group: "Commencer", nav: "Démarrage rapide",
    breadcrumb: "Commencer", title: "Démarrage rapide", subtitle: "Une session ouverte en deux appels, sans mot de passe.",
    sections: [
      { id: "prerequis", heading: "Prérequis", blocks: [
        { t: "p", v: "Un client HTTP capable de conserver les cookies. L'API n'utilise ni clé d'API, ni jeton porteur, ni mot de passe : vous demandez un lien de connexion, et la session vit ensuite dans un cookie `HttpOnly` déposé par le serveur." },
        { t: "note", v: "Toute requête qui modifie des données (POST, PUT, PATCH, DELETE) doit porter un en-tête `Origin` autorisé. C'est notre protection contre les requêtes inter-sites. Sans cet en-tête, la réponse est un 403." },
      ]},
      { id: "demander", heading: "1. Demander un lien", blocks: [
        { t: "code", lang: "bash", v: `curl -X POST ${"https://api.performcustom.com"}/api/auth/magic-link \\
  -H "Content-Type: application/json" \\
  -H "Origin: https://performcustom.com" \\
  -d '{"email": "vous@exemple.fr"}'` },
        { t: "p", v: "Réponse `202` : `{\"status\": \"sent\", \"expires_in_minutes\": 15}`. La réponse est identique que l'adresse soit déjà inscrite ou non — impossible de découvrir qui possède un compte." },
        { t: "p", v: "Le message reçu contient un lien de la forme `https://performcustom.com/auth/verifier?token=…`." },
      ]},
      { id: "verifier", heading: "2. Échanger le jeton contre une session", blocks: [
        { t: "code", lang: "bash", v: `curl -X POST ${"https://api.performcustom.com"}/api/auth/verify \\
  -H "Content-Type: application/json" \\
  -H "Origin: https://performcustom.com" \\
  -c cookies.txt \\
  -d '{"token": "LE_JETON_DU_LIEN"}'` },
        { t: "p", v: "Réponse `200` avec votre utilisateur, et un cookie `perform_session` valable 30 jours. **Le compte est créé à ce moment-là** s'il n'existait pas : il n'y a pas d'inscription séparée." },
        { t: "code", lang: "json", v: `{
  "id": "66f1c0a3e4b0a1d2c3e4f5a6",
  "email": "vous@exemple.fr",
  "username": null,
  "role": "player",
  "onboarded": false,
  "completed_steps": []
}` },
        { t: "note", kind: "info", v: "Un lien ne fonctionne **qu'une seule fois** et expire au bout de 15 minutes. Un second appel avec le même jeton renvoie un `400`." },
      ]},
      { id: "utiliser", heading: "3. Utiliser la session", blocks: [
        { t: "code", lang: "bash", v: `curl ${"https://api.performcustom.com"}/api/auth/me -b cookies.txt

# Compléter le profil
curl -X PATCH ${"https://api.performcustom.com"}/api/me \\
  -H "Content-Type: application/json" \\
  -H "Origin: https://performcustom.com" -b cookies.txt \\
  -d '{"username": "votre_pseudo", "game": "Counter-Strike 2", "onboarded": true}'` },
      ]},
      { id: "fin", heading: "4. Fermer la session", blocks: [
        { t: "code", lang: "bash", v: `curl -X POST ${"https://api.performcustom.com"}/api/auth/logout \\
  -H "Origin: https://performcustom.com" -b cookies.txt` },
        { t: "p", v: "Réponse `204`. La session est supprimée côté serveur, pas seulement côté client : le cookie devient inutilisable même si quelqu'un l'a copié." },
      ]},
    ],
  },
  {
    slug: "concepts", tab: "overview", group: "Concepts", nav: "Concepts clés",
    breadcrumb: "Concepts", title: "Concepts clés", subtitle: "Le vocabulaire de la plateforme.",
    sections: [
      { id: "objets", heading: "Les objets", blocks: [
        { t: "table", head: ["Objet", "Description"], rows: [
          ["Utilisateur", "Un compte. Porte un e-mail unique, un pseudo unique et un rôle."],
          ["Session", "Une connexion active. Identifiant opaque, durée 30 jours, révocable."],
          ["Lien de connexion", "Jeton à usage unique valable 15 minutes, envoyé par e-mail."],
          ["Coach", "Un utilisateur qui publie un profil, des créneaux et des tarifs."],
          ["Séance", "Un rendez-vous entre un joueur et un coach, avec un objectif."],
        ]},
      ]},
      { id: "roles", heading: "Rôles", blocks: [
        { t: "p", v: "Tout compte créé démarre avec le rôle `player`. Deux autres rôles existent :" },
        { t: "ul", v: [
          "`coach` — accès à l'espace coach, aux créneaux et aux revenus.",
          "`admin` — accès à l'administration. Attribué manuellement, jamais par inscription.",
        ]},
        { t: "note", v: "Le rôle n'est jamais accepté depuis le client. Le champ `role` d'une requête d'inscription est ignoré." },
      ]},
      { id: "identifiants", heading: "Identifiants", blocks: [
        { t: "p", v: "Les objets sont identifiés par une chaîne hexadécimale de 24 caractères (ObjectId MongoDB), exposée sous la clé `id`. Ne construisez jamais d'identifiant vous-même : traitez-les comme opaques." },
      ]},
    ],
  },
  {
    slug: "environnements", tab: "overview", group: "Concepts", nav: "Environnements",
    breadcrumb: "Concepts", title: "Environnements", subtitle: "Où pointer vos requêtes.",
    sections: [
      { id: "urls", heading: "Adresses", blocks: [
        { t: "table", head: ["Environnement", "Adresse"], rows: [
          ["Production", "`https://api.performcustom.com`"],
          ["Local", "`http://localhost:8001`"],
        ]},
      ]},
      { id: "docs-openapi", heading: "Schéma OpenAPI", blocks: [
        { t: "p", v: "En développement local, le schéma interactif est servi sur `/docs`. En production il est désactivé : exposer la surface complète de l'API n'apporte rien à un attaquant." },
      ]},
      { id: "sante", heading: "Sonde de santé", blocks: [
        { t: "endpoint", verb: "GET", path: "/api/health", note: "Sans authentification" },
        { t: "p", v: "Renvoie `{\"status\": \"ok\", \"db\": \"ok\"}` quand la base répond, sinon un `503`. C'est la sonde utilisée par notre page d'état et par le redéploiement." },
      ]},
    ],
  },
  {
    slug: "api/authentification", tab: "api", group: "Démarrer", nav: "Authentification",
    breadcrumb: "API REST", title: "Authentification", subtitle: "Sessions par cookie, sans jeton à gérer.",
    sections: [
      { id: "modele", heading: "Le modèle", blocks: [
        { t: "p", v: "Perform n'utilise ni clé d'API ni JWT. Vous ouvrez une session, le serveur dépose un cookie, vos requêtes suivantes l'envoient automatiquement. C'est plus sûr qu'un jeton en JavaScript : le cookie est `HttpOnly`, donc inaccessible au code de la page, ce qui neutralise le vol de session par injection de script." },
      ]},
      { id: "cookie", heading: "Le cookie", blocks: [
        { t: "table", head: ["Attribut", "Valeur"], rows: [
          ["Nom", "`perform_session`"],
          ["HttpOnly", "oui — inaccessible en JavaScript"],
          ["Secure", "oui en production — jamais transmis en clair"],
          ["SameSite", "`None` tant que l'app et l'API sont sur des domaines distincts"],
          ["Durée", "30 jours"],
        ]},
      ]},
      { id: "origin", heading: "Vérification de l'origine", blocks: [
        { t: "p", v: "Parce que le navigateur envoie le cookie automatiquement, un site tiers pourrait déclencher une requête en votre nom. Nous l'empêchons en exigeant un en-tête `Origin` connu sur toute requête modifiante." },
        { t: "code", lang: "bash", v: `# Refusé — 403 « Origine non autorisée »
curl -X POST ${"https://api.performcustom.com"}/api/auth/magic-link -d '...'

# Accepté
curl -X POST ${"https://api.performcustom.com"}/api/auth/magic-link \\
  -H "Origin: https://performcustom.com" -d '...'` },
      ]},
      { id: "js", heading: "Depuis le navigateur", blocks: [
        { t: "p", v: "Les requêtes doivent inclure les identifiants, sinon le cookie n'est ni envoyé ni reçu :" },
        { t: "code", lang: "js", v: `// 1. Demander le lien
await fetch("${"https://api.performcustom.com"}/api/auth/magic-link", {
  method: "POST",
  credentials: "include",          // indispensable
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({ email }),
});

// 2. Sur la page pointée par le lien, échanger le jeton
const r = await fetch("${"https://api.performcustom.com"}/api/auth/verify", {
  method: "POST",
  credentials: "include",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({ token }),
});
const user = await r.json();` },
      ]},
    ],
  },
  {
    slug: "api/reference", tab: "api", group: "Référence", nav: "Endpoints",
    breadcrumb: "API REST", title: "Référence des endpoints", subtitle: "Tout ce que l'API expose aujourd'hui.",
    sections: [
      { id: "auth", heading: "Authentification", blocks: [
        { t: "endpoint", verb: "POST", path: "/api/auth/magic-link", note: "202 · envoie un lien" },
        { t: "p", v: "Corps : `email`. Envoie un lien de connexion valable 15 minutes. Réponse identique que l'adresse soit connue ou non. Limité à 5 requêtes par minute et par IP." },
        { t: "endpoint", verb: "POST", path: "/api/auth/verify", note: "200 · ouvre une session" },
        { t: "p", v: "Corps : `token` (celui du lien). Crée le compte au premier usage, ouvre la session et dépose le cookie. `400` si le lien est expiré, inconnu ou déjà consommé. Limité à 20 requêtes par minute et par IP." },
        { t: "endpoint", verb: "GET", path: "/api/auth/me", note: "200 · utilisateur courant" },
        { t: "p", v: "`401` sans session valide." },
        { t: "endpoint", verb: "POST", path: "/api/auth/logout", note: "204 · ferme la session" },
        { t: "p", v: "Supprime la session en base et efface le cookie. Renvoie `204` même sans session active." },
        { t: "note", kind: "info", v: "Il n'existe **ni `/register` ni `/login`** : l'authentification par mot de passe a été retirée. Ces chemins renvoient un `404`." },
      ]},
      { id: "profil", heading: "Profil et onboarding", blocks: [
        { t: "endpoint", verb: "PATCH", path: "/api/me", note: "200 · met à jour le profil" },
        { t: "p", v: "Champs acceptés : `username`, `first_name`, `last_name`, `game`, `rank`, `goal`, `role_intent` (`player` ou `coach`), `newsletter`, `onboarded`. `409` si le pseudo est déjà pris, casse ignorée. `422` si le pseudo contient autre chose que lettres, chiffres, `_`, `.` ou `-`." },
        { t: "endpoint", verb: "GET", path: "/api/me/steps", note: "200 · étapes de prise en main" },
        { t: "endpoint", verb: "POST", path: "/api/me/steps", note: "200 · valide une étape" },
        { t: "p", v: "Corps : `key`. Rejouer la même étape ne la compte pas deux fois. `422` si la clé est inconnue." },
        { t: "endpoint", verb: "PUT", path: "/api/me/avatar", note: "200 · dépose une photo" },
        { t: "p", v: "Corps : `data`, une data URL en base64 (`image/jpeg`, `image/png` ou `image/webp`). 400 Ko maximum après décodage — redimensionnez côté client. Renvoie `avatar_url`." },
        { t: "endpoint", verb: "DELETE", path: "/api/me/avatar", note: "204 · retire la photo" },
        { t: "endpoint", verb: "GET", path: "/api/avatars/{user_id}", note: "200 · sert l'image" },
        { t: "p", v: "Sans authentification. `404` si l'utilisateur n'a pas de photo." },
      ]},
      { id: "service", heading: "Service", blocks: [
        { t: "endpoint", verb: "GET", path: "/api/", note: "200 · test de vie" },
        { t: "endpoint", verb: "GET", path: "/api/health", note: "200 ou 503 · état de la base" },
      ]},
      { id: "user", heading: "Objet utilisateur", blocks: [
        { t: "code", lang: "json", v: `{
  "id": "string (24 hex)",
  "email": "string",
  "username": "string | null",
  "role": "player | coach | admin",
  "created_at": "string (ISO 8601)",
  "first_name": "string | null",
  "last_name": "string | null",
  "avatar_url": "string | null",
  "game": "string | null",
  "rank": "string | null",
  "goal": "string | null",
  "role_intent": "player | coach | null",
  "newsletter": false,
  "onboarded": false,
  "completed_steps": []
}` },
        { t: "note", kind: "info", v: "Aucun jeton de session n'est exposé par l'API, dans aucune circonstance. Il n'existe plus de hachage de mot de passe : les mots de passe ont été retirés du produit." },
      ]},
    ],
  },
  {
    slug: "api/erreurs", tab: "api", group: "Référence", nav: "Erreurs",
    breadcrumb: "API REST", title: "Erreurs", subtitle: "Un format unique, des codes prévisibles.",
    sections: [
      { id: "format", heading: "Format", blocks: [
        { t: "p", v: "Toute erreur renvoie un JSON avec une seule clé `detail`, en français, affichable tel quel à l'utilisateur." },
        { t: "code", lang: "json", v: `{ "detail": "Ce lien est invalide ou a déjà été utilisé." }` },
        { t: "p", v: "Les erreurs de validation (`422`) suivent le format de FastAPI : `detail` est alors un tableau d'objets décrivant chaque champ fautif." },
      ]},
      { id: "codes", heading: "Codes", blocks: [
        { t: "table", head: ["Code", "Signification"], rows: [
          ["`401`", "Session absente ou expirée."],
          ["`403`", "En-tête `Origin` absent ou non autorisé."],
          ["`400`", "Lien de connexion invalide, expiré ou déjà utilisé."],
          ["`409`", "Pseudo déjà utilisé."],
          ["`422`", "Corps de requête invalide — détail par champ."],
          ["`429`", "Trop de tentatives. En-tête `Retry-After` en secondes."],
          ["`503`", "Base de données injoignable."],
        ]},
      ]},
    ],
  },
  {
    slug: "api/limites", tab: "api", group: "Référence", nav: "Limites de débit",
    breadcrumb: "API REST", title: "Limites de débit", subtitle: "Ce qui est plafonné, et comment réagir.",
    sections: [
      { id: "quotas", heading: "Quotas", blocks: [
        { t: "table", head: ["Endpoint", "Limite"], rows: [
          ["`POST /api/auth/magic-link`", "5 requêtes par minute et par IP"],
          ["`POST /api/auth/verify`", "20 requêtes par minute et par IP"],
          ["Autres endpoints", "Pas de limite fixe à ce jour"],
        ]},
        { t: "p", v: "La limite protège surtout contre l'essai systématique de mots de passe. Elle est calculée sur l'adresse IP réelle, lue derrière notre proxy." },
      ]},
      { id: "depassement", heading: "En cas de dépassement", blocks: [
        { t: "code", lang: "http", v: `HTTP/1.1 429 Too Many Requests
Retry-After: 60

{ "detail": "Trop de tentatives. Réessayez dans une minute." }` },
        { t: "p", v: "Respectez `Retry-After` plutôt que de réessayer immédiatement. Un client qui insiste reste bloqué." },
      ]},
    ],
  },
  {
    slug: "securite/sessions", tab: "security", group: "Modèle", nav: "Sessions",
    breadcrumb: "Sécurité", title: "Modèle de session", subtitle: "Pourquoi nous ne stockons pas vos jetons.",
    sections: [
      { id: "opaque", heading: "Jetons opaques", blocks: [
        { t: "p", v: "À l'ouverture d'une session, le serveur tire 32 octets aléatoires et vous les envoie dans le cookie. En base, il n'enregistre que l'empreinte SHA-256 de ce jeton." },
        { t: "p", v: "La conséquence est directe : **une copie de notre base de données ne permet de rejouer aucune session.** Les empreintes ne se remontent pas." },
      ]},
      { id: "expiration", heading: "Expiration et révocation", blocks: [
        { t: "ul", v: [
          "Durée de vie : 30 jours à compter de l'ouverture.",
          "Un index TTL supprime automatiquement les sessions expirées côté base.",
          "La déconnexion supprime le document de session : le cookie devient inerte immédiatement.",
        ]},
      ]},
      { id: "liens", heading: "Liens de connexion", blocks: [
        { t: "p", v: "Perform n'utilise **aucun mot de passe**. Il n'y a donc rien à hacher, rien a stocker et rien a se faire voler de ce côté." },
        { t: "p", v: "Un lien de connexion est un jeton aléatoire de 32 octets, valable 15 minutes, utilisable une seule fois. Comme pour les sessions, la base ne conserve que son empreinte SHA-256." },
        { t: "note", v: "En contrepartie, la sécurité du compte est celle de la boîte mail : quiconque y accède peut demander un lien. Protégez votre messagerie avec un second facteur." },
      ]},
      { id: "suspension", heading: "Comptes suspendus", blocks: [
        { t: "p", v: "Suspendre un compte révoque ses sessions **et** empêche d'en ouvrir une nouvelle : un compte suspendu qui redemande un lien obtient un `403`." },
      ]},
    ],
  },
  {
    slug: "securite/donnees", tab: "security", group: "Modèle", nav: "Données",
    breadcrumb: "Sécurité", title: "Données", subtitle: "Ce que nous conservons, et pourquoi.",
    sections: [
      { id: "quoi", heading: "Ce que nous stockons", blocks: [
        { t: "table", head: ["Donnée", "Raison", "Conservation"], rows: [
          ["E-mail", "Identifiant de connexion, contact", "Durée du compte"],
          ["Pseudo", "Affichage public", "Durée du compte"],
          ["Sessions", "Maintien de la connexion", "30 jours, puis suppression automatique"],
          ["Agent utilisateur", "Reconnaître une session suspecte", "Avec la session"],
        ]},
      ]},
      { id: "pas", heading: "Ce que nous ne stockons pas", blocks: [
        { t: "ul", v: [
          "Aucune coordonnée bancaire : les paiements passeront par un prestataire agréé.",
          "Aucun traceur publicitaire tiers.",
          "Aucun mot de passe, en clair ou haché : le produit n'en utilise pas.",
          "Aucun jeton de connexion dans nos journaux, applicatifs comme d'accès.",
        ]},
      ]},
      { id: "plus", heading: "Pour aller plus loin", blocks: [
        { t: "p", v: "Le [Trust Center](/trust-center) détaille notre posture de sécurité, et la [politique de confidentialité](/legal/confidentialite) précise vos droits et la manière de les exercer." },
      ]},
    ],
  },
];

export const DOC_SHORTCUTS = [
  { icon: "Code2", label: "Espace développeur", to: "/developpeurs" },
  { icon: "LifeBuoy", label: "Support", to: "/aide" },
];

export const findDoc = slug => DOCS.find(d => d.slug === (slug || ""));
